import * as Yup from "yup";

export const basicFormValidations = (employeeId) =>
  Yup.object().shape({
    first_name: Yup.string()
      .trim()
      .required("First Name is required")
      .matches(
        /^[A-Za-z\s]+$/,
        "First Name can only contain letters and spaces"
      ),

    last_name: Yup.string()
      .trim()
      .required("Last Name is required")
      .matches(
        /^[A-Za-z\s]+$/,
        "Last Name can only contain letters and spaces"
      ),

    email: Yup.string()
      .trim()
      .email("Invalid email address")
      .required("Email is required"),

    password: Yup.string().when([], {
      is: () => !employeeId,
      then: (schema) =>
        schema
          .required("Password is required")
          .min(8, "Password must be at least 8 characters"),
      otherwise: (schema) => schema.notRequired(),
    }),
    role_id: Yup.string().required("Role is required"),

    mobile_number: Yup.string()
      .trim()
      .matches(
        /^(?:\+(\d{1,3})\s?)?(\(\d{3}\)\s?\d{3}-\d{4})$/,
        "Invalid phone number format. { e.g., +1 (123) 456-7890 or (123) 456-7890 }"
      ),

    landline_number: Yup.string()
      .trim()
      .matches(
        /^(?:\+(\d{1,3})\s?)?(\(\d{3}\)\s?\d{3}-\d{4})$/,
        "Invalid phone number format. { e.g., +1 (123) 456-7890 or (123) 456-7890 }"
      ),

    emergency_contact_name: Yup.string()
      .trim()
      .required("Emergency contact name is required"),

    emergency_contact_number: Yup.string()
      .trim()
      .matches(
        /^(?:\+(\d{1,3})\s?)?(\(\d{3}\)\s?\d{3}-\d{4})$/,
        "Invalid phone number format. { e.g., +1 (123) 456-7890 or (123) 456-7890 }"
      ),

    country_id: Yup.string().trim().required("Please select your country"),
    province_id: Yup.string().trim().required("Please select your province"),
    city_id: Yup.string().trim().required("Please select your city"),

    date_of_birth: Yup.date()
      .nullable()
      .max(new Date(), "Date of Birth cannot be in the future")
      .test("age-limit", "You must be at least 18 years old", (value) => {
        if (!value) return true;
        const today = new Date();
        const birthDate = new Date(value);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (
          monthDiff < 0 ||
          (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ) {
          age--;
        }
        return age >= 18;
      }),
  });

export const serviceInfoValidationSchema = Yup.object().shape({
  hiring_date: Yup.date()
    .required("Hiring Date is required")
    .max(new Date(), "Hiring Date cannot be in the future"),

  confirmation_date: Yup.date()
    .required("Confirmation Date is required")
    .min(Yup.ref("hiring_date"), "Confirmation Date must be after Hiring Date"),

  joining_date: Yup.date()
    .required("Joining Date is required")
    .min(Yup.ref("hiring_date"), "Joining Date must be after Hiring Date"),

  regular_date: Yup.date()
    .required("Regular Date is required")
    .min(Yup.ref("joining_date"), "Regular Date must be after Joining Date"),

  probation_end_date: Yup.date()
    .required("Probation End Date is required")
    .min(
      Yup.ref("joining_date"),
      "Probation End Date must be after Joining Date"
    ),

  left_date: Yup.date()
    .nullable()
    .test(
      "is-after-regular",
      "Left Date must be after Regular Date",
      function (value) {
        const regularDate = this.parent.regular_date;
        return !value || (regularDate && value >= regularDate);
      }
    )
    .test(
      "is-future",
      "Left Date cannot be in the future",
      (value) => !value || value <= new Date()
    ),
});

export const CompanyInfoValidationSchema = Yup.object().shape({
  department_id: Yup.string().required("Department is required"),
  designation_id: Yup.string().required("Designation is required"),
  basic_salary: Yup.number()
    .typeError("Basic Salary must be a number")
    .required("Basic Salary is required")
    .min(1, "Basic Salary must be greater than zero"),
  gross_salary: Yup.number()
    .typeError("Gross Salary must be a number")
    .required("Gross Salary is required")
    .min(
      Yup.ref("basic_salary"),
      "Gross Salary must be greater than or equal to Basic Salary"
    ),
  hourly_rate: Yup.number()
    .typeError("Hourly Rate must be a number")
    .required("Hourly Rate is required")
    .min(0, "Hourly Rate must be a positive number"),
  bank_account_title: Yup.string()
    .required("Bank Account Title is required")
    .matches(
      /^[a-zA-Z\s]+$/,
      "Bank Account Title must contain only letters and spaces"
    ),
  IBAN: Yup.string()
    .required("IBAN is required")
    .matches(/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/, "Invalid IBAN format"),
});

export const documentsValidationSchema = Yup.object()
  .shape({
    documentName: Yup.string().nullable(),
    documentType: Yup.string().nullable(),
    document: Yup.mixed().nullable(),
  })
  .test(
    "all-or-none",
    "If one document field is provided, all are required",
    function (values) {
      const { documentName, documentType, document } = values;

      // If all fields are empty, return true (no validation error)
      if (!documentName && !documentType && !document) {
        return true;
      }
      // If any field is filled, all must be filled
      if (!documentName || !documentType || !document) {
        return this.createError({
          path: "document",
          message:
            "If one field is provided, all (Name, Type, and Document) must be provided.",
        });
      }

      return true;
    }
  );
